import {
  Button,
  ButtonGroup,
  CalendarMdIcon,
  CloseSmIcon,
  DatePicker,
  DatePickerCalendar,
  DatePickerContent,
  DatePickerPopover,
  DatePickerTrigger,
  IconButton,
  PopoverAnchor,
} from '@crono-one/design-system';
import { format, startOfDay } from 'date-fns';
import { useState } from 'react';

//Sunday and Saturday. The account's working days are a back-end concern and are
//not exposed to the client, so the picker assumes the common pair
const WEEKEND_DAYS = [0, 6];

//How far ahead and behind the calendar may be paged. Matches the app's
//`FeConstants.datePickerDefaultYearsOffset`, kept here so the component carries no
//host-specific import
const DEFAULT_YEARS_OFFSET = 5;

const getDateYearsFromNow = (years: number) => {
  const date = new Date();
  date.setUTCFullYear(date.getUTCFullYear() + years);
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
  return date;
};

interface SequenceStartDatePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  yearsOffset?: number;
}

/**
 * The design system's `ButtonDatePicker` hardcodes "Pick date" as its single-mode
 * placeholder with no prop to override it, so the primitives are composed here to get
 * the designed copy while still using the design system's calendar.
 */
const SequenceStartDatePicker = ({
  value,
  onChange,
  yearsOffset = DEFAULT_YEARS_OFFSET,
}: SequenceStartDatePickerProps) => {
  //Frozen at mount so the bounds cannot drift mid-interaction, and to keep the
  //component pure: this picker only lives as long as the popup that opened it
  const [bounds] = useState(() => ({
    today: startOfDay(new Date()),
    startMonth: getDateYearsFromNow(-yearsOffset),
    endMonth: getDateYearsFromNow(+yearsOffset),
  }));

  return (
    <DatePicker
      mode="single"
      selected={value}
      onSelect={onChange}
      numberOfMonths={2}
      //Past days, and weekends: a sequence started on a Saturday would only
      //produce its first task on the Monday anyway, so the day is not offered
      disabled={[{ before: bounds.today }, { dayOfWeek: WEEKEND_DAYS }]}
      startMonth={bounds.startMonth}
      endMonth={bounds.endMonth}
    >
      <DatePickerPopover>
        <PopoverAnchor>
          <ButtonGroup className="min-w-0 max-w-full">
            <DatePickerTrigger>
              <Button
                variant="outline"
                tone="gray"
                shape="rounded"
                size="sm"
                className="min-w-0 pl-1 pr-2 font-normal"
              >
                <CalendarMdIcon className="shrink-0" />
                <span className="truncate">
                  {value
                    ? format(value, 'MMM dd, yyyy')
                    : 'Set sequence start date'}
                </span>
              </Button>
            </DatePickerTrigger>
            {!!value && (
              <IconButton
                variant="outline"
                tone="lightGray"
                shape="rounded"
                size="sm"
                className="shrink-0 [&>svg]:size-4"
                label="Clear start date"
                icon={CloseSmIcon}
                onClick={() => onChange(undefined)}
              />
            )}
          </ButtonGroup>
        </PopoverAnchor>
        <DatePickerContent
          side="top"
          align="start"
          className="sequence-start-date-calendar z-50000"
        >
          <DatePickerCalendar />
        </DatePickerContent>
      </DatePickerPopover>
    </DatePicker>
  );
};

export default SequenceStartDatePicker;
